import { useState, useEffect, useCallback } from 'react';
import { Key, Webhook, Copy, CheckCircle, Plus, Trash2, RefreshCw, X, ChevronRight, Download } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────

interface ApiKey {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  active: boolean;
  last_used?: string;
  created_at: string;
}

interface WebhookConfig {
  id: string;
  url: string;
  secret: string;
  events: string[];
  active: boolean;
  last_delivery_status?: 'success' | 'failed';
  last_delivery_at?: string;
  delivery_count: number;
  failure_count: number;
}

interface WebhookDelivery {
  id: string;
  event_type: string;
  status: 'success' | 'failed';
  response_code?: number;
  response_time_ms?: number;
  payload_preview: string;
  error?: string;
  created_at: string;
}

const ALL_SCOPES = [
  'employees_read',
  'employees_write',
  'time_off_read',
  'time_off_write',
  'reviews_read',
];

const ALL_EVENTS = [
  'employee.created',
  'employee.updated',
  'employee.terminated',
  'time_off.approved',
  'time_off.denied',
  'review.completed',
];

// ─── Generate Key Modal ───────────────────────────────────────

function GenerateKeyModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['employees_read']);
  const [saving, setSaving] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  function toggleScope(scope: string) {
    setScopes(prev => prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]);
  }

  async function generate() {
    if (!name.trim() || scopes.length === 0) return;
    setSaving(true);
    try {
      const res = await fetch('/api/people/hris/api-keys', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, scopes }),
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json() as { key: string };
      setNewKey(data.key);
      onCreated();
    } catch { /* silent */ } finally {
      setSaving(false);
    }
  }

  function copy() {
    if (!newKey) return;
    void navigator.clipboard.writeText(newKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="glass rounded-xl p-6 w-full max-w-md space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Generate API Key</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>

        {newKey ? (
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <p className="text-xs text-amber-400 mb-2 font-medium">Copy your key now — it won&apos;t be shown again.</p>
              <div className="flex items-center gap-2 bg-surface-3 rounded-lg p-2">
                <code className="flex-1 text-xs text-foreground font-mono break-all">{newKey}</code>
                <button onClick={copy} className="shrink-0 text-amber-400 hover:text-amber-300">
                  {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button onClick={onClose} className="w-full py-2 rounded-lg bg-surface-3 hover:bg-surface-2 text-sm text-foreground">Done</button>
          </div>
        ) : (
          <>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Key Name</label>
              <input value={name} onChange={e => setName(e.target.value)}
                placeholder="e.g. BambooHR Integration"
                className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-2">Scopes</label>
              <div className="space-y-1.5">
                {ALL_SCOPES.map(scope => (
                  <label key={scope} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={scopes.includes(scope)} onChange={() => toggleScope(scope)}
                      className="rounded border-border" />
                    <span className="text-sm text-foreground font-mono text-xs">{scope}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-surface-3">Cancel</button>
              <button onClick={() => void generate()} disabled={!name.trim() || scopes.length === 0 || saving}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-amber-500 text-foreground text-sm hover:bg-amber-600 disabled:opacity-50">
                <Key className="w-3.5 h-3.5" />
                {saving ? 'Generating...' : 'Generate'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Add Webhook Modal ────────────────────────────────────────

function AddWebhookModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>(['employee.created']);
  const [saving, setSaving] = useState(false);

  function toggleEvent(event: string) {
    setEvents(prev => prev.includes(event) ? prev.filter(e => e !== event) : [...prev, event]);
  }

  async function save() {
    if (!url.trim() || events.length === 0) return;
    setSaving(true);
    try {
      const res = await fetch('/api/people/hris/webhooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, events, active: true }),
      });
      if (!res.ok) throw new Error('Failed');
      onCreated();
      onClose();
    } catch { /* silent */ } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="glass rounded-xl p-6 w-full max-w-md space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Add Webhook</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Payload URL</label>
          <input value={url} onChange={e => setUrl(e.target.value)}
            placeholder="https://your-app.com/webhook"
            className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" />
          <p className="text-xs text-muted-foreground mt-1">HMAC-SHA256 signature sent in X-People-Signature header. Secret auto-generated.</p>
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-2">Events</label>
          <div className="space-y-1.5">
            {ALL_EVENTS.map(event => (
              <label key={event} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={events.includes(event)} onChange={() => toggleEvent(event)}
                  className="rounded border-border" />
                <span className="text-xs font-mono text-foreground">{event}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-surface-3">Cancel</button>
          <button onClick={() => void save()} disabled={!url.trim() || events.length === 0 || saving}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-amber-500 text-foreground text-sm hover:bg-amber-600 disabled:opacity-50">
            <Webhook className="w-3.5 h-3.5" />
            {saving ? 'Adding...' : 'Add Webhook'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function HrisApiPage() {
  const [activeTab, setActiveTab] = useState<'keys' | 'webhooks' | 'openapi'>('keys');
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [webhooks, setWebhooks] = useState<WebhookConfig[]>([]);
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [selectedWebhook, setSelectedWebhook] = useState<string | null>(null);
  const [openApiSpec, setOpenApiSpec] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showGenKey, setShowGenKey] = useState(false);
  const [showAddWebhook, setShowAddWebhook] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);

  const loadKeys = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/people/hris/api-keys');
      if (res.ok) { const d = await res.json() as { keys: ApiKey[] }; setApiKeys(d.keys ?? []); }
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  const loadWebhooks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/people/hris/webhooks');
      if (res.ok) { const d = await res.json() as { webhooks: WebhookConfig[] }; setWebhooks(d.webhooks ?? []); }
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  const loadOpenApi = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/people/hris/openapi');
      if (res.ok) { const d = await res.json(); setOpenApiSpec(JSON.stringify(d, null, 2)); }
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'keys') void loadKeys();
    if (activeTab === 'webhooks') void loadWebhooks();
    if (activeTab === 'openapi') void loadOpenApi();
  }, [activeTab, loadKeys, loadWebhooks, loadOpenApi]);

  async function revokeKey(id: string) {
    await fetch(`/api/people/hris/api-keys/${id}`, { method: 'DELETE' });
    void loadKeys();
  }

  async function deleteWebhook(id: string) {
    await fetch(`/api/people/hris/webhooks/${id}`, { method: 'DELETE' });
    void loadWebhooks();
  }

  async function testWebhook(id: string) {
    setTesting(id);
    try {
      await fetch(`/api/people/hris/webhooks/${id}/test`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      void loadWebhooks();
    } catch { /* silent */ } finally { setTesting(null); }
  }

  async function loadDeliveries(webhookId: string) {
    setSelectedWebhook(webhookId);
    try {
      const res = await fetch(`/api/people/hris/webhooks/${webhookId}/deliveries`);
      if (res.ok) { const d = await res.json() as { deliveries: WebhookDelivery[] }; setDeliveries(d.deliveries ?? []); }
    } catch { /* silent */ }
  }

  function downloadSpec() {
    const blob = new Blob([openApiSpec], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'theonepeople-hris-api.json'; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Key className="w-5 h-5 text-amber-500" />
          <h1 className="text-xl font-bold text-foreground">HRIS API &amp; Webhooks</h1>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === 'keys' && (
            <button onClick={() => setShowGenKey(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-foreground hover:bg-amber-600 text-sm">
              <Plus className="w-3.5 h-3.5" /> Generate Key
            </button>
          )}
          {activeTab === 'webhooks' && (
            <button onClick={() => setShowAddWebhook(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-foreground hover:bg-amber-600 text-sm">
              <Plus className="w-3.5 h-3.5" /> Add Webhook
            </button>
          )}
          <button onClick={() => {
            if (activeTab === 'keys') void loadKeys();
            if (activeTab === 'webhooks') void loadWebhooks();
          }} className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-3">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-surface-3 rounded-lg p-1 w-fit">
        {[
          { id: 'keys' as const, label: 'API Keys', icon: Key },
          { id: 'webhooks' as const, label: 'Webhooks', icon: Webhook },
          { id: 'openapi' as const, label: 'OpenAPI Spec', icon: Download },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm transition-colors ${activeTab === tab.id ? 'bg-amber-500/20 text-amber-400' : 'text-muted-foreground hover:text-foreground'}`}>
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* API Keys */}
      {activeTab === 'keys' && (
        <div className="glass rounded-xl overflow-hidden">
          {loading ? <p className="p-4 text-sm text-muted-foreground">Loading...</p> : apiKeys.length === 0 ? (
            <div className="p-12 text-center">
              <Key className="w-12 h-12 text-amber-500/20 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No API keys yet. Generate one to integrate with HRIS.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface-2">
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Key</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Scopes</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground">Last Used</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {apiKeys.map(key => (
                  <tr key={key.id} className="border-b border-border/50 hover:bg-surface-3/50">
                    <td className="px-4 py-3 font-medium text-foreground">{key.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{key.key_prefix}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {key.scopes.map(s => (
                          <span key={s} className="px-1.5 py-0.5 rounded text-xs bg-surface-3 text-muted-foreground font-mono">{s}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{key.last_used ? new Date(key.last_used).toLocaleDateString() : '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => void revokeKey(key.id)}
                        className="p-1 text-muted-foreground hover:text-red-400" title="Revoke key">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Webhooks */}
      {activeTab === 'webhooks' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-3">
            {loading ? <p className="text-sm text-muted-foreground">Loading...</p> : webhooks.length === 0 ? (
              <div className="glass rounded-xl p-12 text-center">
                <Webhook className="w-12 h-12 text-amber-500/20 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No webhooks configured yet.</p>
              </div>
            ) : (
              webhooks.map(wh => (
                <div key={wh.id} className="glass rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-mono text-foreground truncate">{wh.url}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {wh.events.map(e => (
                          <span key={e} className="px-1.5 py-0.5 rounded text-xs bg-surface-3 text-muted-foreground">{e}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {wh.last_delivery_status === 'success' && <span className="w-2 h-2 rounded-full bg-green-400" title="Last delivery succeeded" />}
                      {wh.last_delivery_status === 'failed' && <span className="w-2 h-2 rounded-full bg-red-400" title="Last delivery failed" />}
                      {!wh.active && <span className="px-1.5 py-0.5 rounded text-xs bg-muted/20 text-muted-foreground">Inactive</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{wh.delivery_count} delivered</span>
                    {wh.failure_count > 0 && <span className="text-red-400">{wh.failure_count} failed</span>}
                    <div className="ml-auto flex gap-1">
                      <button onClick={() => void testWebhook(wh.id)} disabled={testing === wh.id}
                        className="px-2 py-0.5 rounded bg-surface-3 hover:bg-surface-2 text-foreground disabled:opacity-50">
                        {testing === wh.id ? '...' : 'Test'}
                      </button>
                      <button onClick={() => void loadDeliveries(wh.id)}
                        className="flex items-center gap-0.5 px-2 py-0.5 rounded bg-surface-3 hover:bg-surface-2 text-foreground">
                        History <ChevronRight className="w-3 h-3" />
                      </button>
                      <button onClick={() => void deleteWebhook(wh.id)}
                        className="p-1 text-muted-foreground hover:text-red-400">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Delivery History */}
          {selectedWebhook && (
            <div className="glass rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">Delivery History</h3>
                <button onClick={() => setSelectedWebhook(null)} className="text-muted-foreground hover:text-foreground">
                  <X className="w-4 h-4" />
                </button>
              </div>
              {deliveries.length === 0 ? (
                <p className="text-xs text-muted-foreground">No deliveries yet.</p>
              ) : (
                <div className="space-y-2">
                  {deliveries.map(d => (
                    <div key={d.id} className="p-2 rounded-lg bg-surface-3 space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-mono text-muted-foreground">{d.event_type}</span>
                        <div className="flex items-center gap-1">
                          {d.status === 'success'
                            ? <CheckCircle className="w-3 h-3 text-green-400" />
                            : <span className="w-2 h-2 rounded-full bg-red-400" />}
                          {d.response_code && <span className="text-muted-foreground">{d.response_code}</span>}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">{new Date(d.created_at).toLocaleString()}</p>
                      {d.error && <p className="text-xs text-red-400">{d.error}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* OpenAPI Spec */}
      {activeTab === 'openapi' && (
        <div className="glass rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground">OpenAPI 3.0 Specification</h2>
            <button onClick={downloadSpec} disabled={!openApiSpec}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-3 hover:bg-surface-2 text-sm text-foreground disabled:opacity-50">
              <Download className="w-3.5 h-3.5" /> Download JSON
            </button>
          </div>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading spec...</p>
          ) : (
            <pre className="bg-surface-3 rounded-lg p-4 text-xs text-foreground/80 font-mono overflow-auto max-h-[500px] whitespace-pre-wrap">
              {openApiSpec}
            </pre>
          )}
        </div>
      )}

      {showGenKey && <GenerateKeyModal onClose={() => setShowGenKey(false)} onCreated={() => void loadKeys()} />}
      {showAddWebhook && <AddWebhookModal onClose={() => setShowAddWebhook(false)} onCreated={() => void loadWebhooks()} />}
    </div>
  );
}
